"use client";

import React, { useState } from "react";
import Image from "next/image";
import BrochurePopupForm from "@/components/newPageComponent/CommonSection/BrochurePopupForm";

/* ---------------- TYPES ---------------- */
type CardId = "far" | "aud" | "reg" | "bar";

interface ExamPoint {
  icon: string;
  label: string;
}

interface ExamCard {
  id: CardId;
  image: string;
  title: string;
  points: ExamPoint[];
}

/* ---------------- DATA ---------------- */
const examCards: ExamCard[] = [
  {
    id: "far",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/far_new.webp",
    title: "Financial Accounting & Reporting",
    points: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/far_icon_1.webp",
        label: "Lease classification & ROU agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/far_icon_2.webp",
        label: "Revenue recognition 5-step engine",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/far_icon_3.webp",
        label: "Consolidation & elimination workflows",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/far_icon_4.webp",
        label: "GAAP-to-IFRS reconciliation agents",
      },
    ],
  },
  {
    id: "aud",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/aud_new.webp",
    title: "Auditing and Attestation",
    points: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/aud_icon_1.webp",
        label: "Full-population testing engines",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/aud_icon_2.webp",
        label: "Auto-generated audit workpapers",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/aud_icon_3.webp",
        label: "Fraud anomaly detection agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/aud_icon_4.webp",
        label: "Internal control walkthrough copilots",
      },
    ],
  },
  {
    id: "reg",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/reg_new.webp",
    title: "Taxation & Regulation",
    points: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/reg_icon_1.webp",
        label: "Tax return drafting agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/reg_icon_2.webp",
        label: "Filing tracker dashboards",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/reg_icon_3.webp",
        label: "Automated compliance check workflows",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/reg_icon_4.webp",
        label: "Tax law research copilots",
      },
    ],
  },
  {
    id: "bar",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/bar_new.webp",
    title: "Business Analysis & Reporting",
    points: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/bar_icon_1.webp",
        label: "Driver-based forecasting agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/bar_icon_2.webp",
        label: "Variance commentary agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/bar_icon_3.webp",
        label: "Continuous close workflows",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/bar_icon_4.webp",
        label: "Government fund reporting copilots",
      },
    ],
  },
];

/* ---------------- COMPONENT ---------------- */
const CpaEquivalentNew = () => {
  const [isFormPopupOpen, setFormPopupOpen] = useState(false);

  const openFormPopup = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("modal-open", "true");
    }
    setFormPopupOpen(true);
  };

  const closeFormPopup = () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("modal-open");
    }
    setFormPopupOpen(false);
  };

  return (
    <>
      <section className="bg-white py-8 px-4 max-md:bg-[#f3f3f3] max-md:p-4 md:pt-0 pt-4 max-md:pb-7">
        <div className="main_container py-5 bg-white rounded-2xl">
          <div className="flex flex-col items-center text-center">
            <h2 className="m-0 text-[#333] text-[32px] font-bold leading-[1.2] tracking-[-0.02em] max-md:text-[24px]">
              <span className="text-[#3399ff]">CPA = U.S.</span> Equivalent of
              CA
            </h2>

            <p className="mt-8 text-[#999] text-sm font-semibold max-md:mt-2.5">
              4 Exams | 12 Months | Integrated with
              <br className="md:hidden block" /> CAIRA - AI workflows & agents
            </p>

            {/* CARDS */}
            <div className="grid grid-cols-4 justify-center gap-4 w-full mt-8 max-md:flex max-md:overflow-x-auto max-md:gap-3 max-md:px-4 max-md:[scrollbar-width:none] max-md:justify-start">
              {examCards.map((card) => (
                <div
                  key={card.id}
                  className="flex flex-col border border-[#DEE7F5] rounded-[10px] bg-white p-2.5 max-md:w-[270px] max-md:flex-shrink-0"
                >
                  <div className="relative">
                    <Image
                      src={card.image}
                      alt={card.title}
                      width={320}
                      height={180}
                      sizes="(max-width: 768px) 270px, 320px"
                      className="w-full h-auto rounded-lg"
                    />
                    <span className="absolute bottom-2 right-2 text-[10px] font-bold text-white flex items-center">
                      Integrated with
                      <Image
                        src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/starIcon.webp"
                        alt="star"
                        width={12}
                        height={12}
                        className="mx-1 invert brightness-0"
                      />
                      CAIRA
                    </span>
                  </div>

                  <div className="pt-3 flex flex-col gap-3">
                    <h3 className="text-[#0B2A4D] text-base font-bold text-center">
                      {card.title}
                    </h3>

                    <div className="grid grid-cols-2 gap-2">
                      {card.points.map((point) => (
                        <div
                          key={point.label}
                          className="flex flex-col items-center justify-start gap-2 border border-[#E1F3F8] rounded-[8px] bg-white p-[5px] text-center"
                        >
                          <Image
                            src={point.icon}
                            alt={point.label}
                            width={32}
                            height={32}
                            className="h-6 w-6 object-contain"
                          />
                          <p className="text-[11px] md:text-xs leading-tight font-medium text-[#1D1D1D]">
                            {point.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* BUTTON */}
            <button
              className="mt-8 !min-w-[306px] px-[28px] py-[12px] border-0 rounded-tl-[10.85px] rounded-tr-[10.85px] rounded-br-[10.85px] rounded-bl-none bg-[#2a85ff] text-white text-[16px] font-bold leading-none cursor-pointer max-md:w-[calc(100%-32px)] max-md:min-w-0 max-md:px-4 max-md:text-[15px]"
              onClick={openFormPopup}
            >
              Download Brochure
            </button>
          </div>
        </div>
      </section>

      {/* POPUP */}
      {isFormPopupOpen && (
        <BrochurePopupForm pageName="CPA" handleClose={closeFormPopup} />
      )}
    </>
  );
};

export default CpaEquivalentNew;
